/**
 * The only colours the site is allowed to decorate with.
 *
 * Owner's rule, and it is a brand rule rather than a preference: *„kolorystyczne
 * dodatki tylko motyw fioletowo-różowy, czyli gradient FLOU — nie możemy
 * dodawać zielonych, żółtych, niebieskich… dotyczy się całej strony"*.
 *
 * Several grids had drifted off it at once — the equipment categories, the
 * three production specialists, the nine graphics options, the nine metrics on
 * the home page — each one reaching for a fresh hue whenever it needed one
 * more thing to be distinguishable. Nine of those picks were greens, ambers,
 * teals and blues, which is a palette nobody chose: it was the by-product of
 * four files solving the same problem separately.
 *
 * So the palette lives HERE, once, and it is the brand gradient's own arc:
 * hue {@link ARC_START} (violet) through {@link ARC_END} (pink), the same span
 * `--flou-gradient` runs. Nothing outside it goes on a decorative surface.
 *
 * ## Where the separation comes from
 *
 * Sixty-five degrees of hue will not tell nine neighbours apart on its own, so
 * the stops move in LIGHTNESS and CHROMA as well — a pale lilac beside a
 * saturated fuchsia reads as two things even though both are violet-pink. The
 * floor is `0.64`: below that an icon on this near-black page stops reading as
 * a colour and starts reading as a smudge.
 *
 * ## What this does NOT govern
 *
 * Status colour. A validation error is red and a capacity warning is amber
 * because that is what those states mean, not because somebody wanted variety
 * — see `ERROR_TEXT` in the contact form and the admin capacity board. Those
 * are the only warm hues left on the site and they must stay.
 */

/** The violet end of `--flou-gradient`. */
export const ARC_START = 285
/** The pink end. */
export const ARC_END = 352

/**
 * Nine stops, ordered so that ADJACENT ones differ as much as possible.
 *
 * Callers hand out tones by position, and the positions that matter are the
 * ones next to each other in a grid — so this list is not sorted by hue. Going
 * round the arc in order would put the two palest lilacs side by side in every
 * row of three.
 */
/*
 * Every stop fits sRGB. Three of them did not — 300, 292 and 286 asked for
 * 0.006 to 0.022 more chroma than the space holds — so the browser clipped
 * them and the array named a colour nobody saw. Corrected rather than given a
 * wide-gamut twin, because a `@media` block cannot reach a JavaScript string
 * and the difference at this size is under what a screen shows as a change.
 */
export const BRAND_TONES: readonly string[] = [
  'oklch(0.76 0.17 350)',
  'oklch(0.66 0.24 332)',
  'oklch(0.84 0.1 322)',
  'oklch(0.66 0.214 300)',
  'oklch(0.72 0.2 342)',
  'oklch(0.79 0.116 292)',
  'oklch(0.69 0.25 318)',
  'oklch(0.87 0.08 336)',
  'oklch(0.71 0.158 286)',
]

/**
 * A stop by position, wrapping rather than running out.
 *
 * Wrapping is the right failure here: a tenth category added in the panel
 * repeats the first colour, which is a mild collision. Returning `undefined`
 * and falling back to one shared violet would make every new thing look like
 * every other new thing.
 */
export function brandTone(index: number): string {
  const stops = BRAND_TONES
  return stops[((index % stops.length) + stops.length) % stops.length] ?? stops[0] ?? ''
}

/**
 * The hue of an `oklch(L C H)` string, or `null` if it is not one.
 *
 * Exists for the test that holds the rule above: a guard nobody can read off
 * the palette by eye once it is spread across four files.
 */
export function hueOf(tone: string): number | null {
  const match = /^oklch\(\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s*\)$/.exec(tone)
  const hue = match?.[1]
  return hue === undefined ? null : Number(hue)
}

/** Whether a colour sits on the brand's arc. */
export function isBrandTone(tone: string): boolean {
  const hue = hueOf(tone)
  return hue !== null && hue >= ARC_START && hue <= ARC_END
}

/* ── THE ACCENT RAMP ───────────────────────────────────────── */

/**
 * The three stops `ACCENT_TEXT` clips through a headline, as numbers.
 *
 * `bg-gradient-to-r from-… via-… to-…` is a background, so it only ever
 * reaches type that can clip one. Two things on this site cannot: the hero's
 * split headline, whose characters are individually transformed spans that a
 * clipped background tears apart, and the particle wordmark, which is pixels
 * on a canvas. Both were interpolating this ramp from their own private copy
 * of the stops — so the accent existed three times and only one of them was
 * the one the CSS actually used.
 *
 * Left to right, matching the `to-r` direction: pink, magenta, violet.
 */
export const ACCENT_STOPS: ReadonlyArray<{ l: number; c: number; h: number }> = [
  { l: 0.72, c: 0.22, h: 350 },
  { l: 0.66, c: 0.25, h: 320 },
  { l: 0.62, c: 0.24, h: 285 },
]

/**
 * The accent colour at `t` along the ramp, `0` at the pink end and `1` at the
 * violet one. Out-of-range values clamp rather than extrapolate — past the end
 * of the arc is exactly the off-brand hue this file exists to prevent.
 */
export function accentAt(t: number): string {
  const stops = ACCENT_STOPS
  const last = stops.length - 1
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t
  const segment = clamped * last
  const i = Math.min(Math.floor(segment), last - 1)
  const f = segment - i
  const a = stops[i] ?? stops[0]
  const b = stops[i + 1] ?? stops[last]
  if (!a || !b) return ''
  const mix = (from: number, to: number) => from + (to - from) * f
  return `oklch(${mix(a.l, b.l).toFixed(3)} ${mix(a.c, b.c).toFixed(3)} ${mix(a.h, b.h).toFixed(1)})`
}
